/**
 * Custom hook for managing food selection logic
 */

import { useCallback } from 'react';
import { v4 as uuid } from 'uuid';

/**
 * Generate a short 8-character UUID for meals
 * @returns {string} 8-character UUID
 */
const generateShortUuid = () => {
    return uuid().substring(0, 8);
};

/**
 * Determines if a food needs portion information from the user
 * @param {Object} food - The food object
 * @param {string|null} unit - The unit of measurement
 * @param {number|null} grams - The grams amount
 * @param {number|null} milliliters - The milliliters amount
 * @returns {boolean} True if portion info is needed
 */
function needsPortionInfo(food, unit, grams, milliliters) {
    // Need portion info if:
    // 1. No unit is provided, OR
    // 2. Food lacks serving size info (grams/milliliters per serving), OR
    // 3. No specific grams/milliliters amounts are provided
    return !unit || 
           (!food.grams_per_serving && !food.milliliters_per_serving) || 
           (!grams && !milliliters);
}

/**
 * Extracts portion data from a portions object returned by LogPortionsModal
 * @param {Object} portions - The portions object keyed by food UUID
 * @param {string} foodUuid - The UUID of the food to extract
 * @returns {Object|null} Extracted portion data or null if not found
 */
function extractPortionData(portions, foodUuid) {
    const portion = portions[foodUuid];
    if (!portion) return null;

    const loggedUnit = typeof portion.logged_unit === 'object'
        ? portion.logged_unit?.description
        : portion.logged_unit;

    return {
        unit: loggedUnit || portion.unit?.description || '',
        amount: portion.logged_amount || 1,
        grams: portion.logged_grams,
        milliliters: portion.logged_milliliters
    };
}

/**
 * Creates a meal object for a food item
 * @param {Object} food - The food object
 * @param {Object} portionInfo - Object containing unit, amount, grams, milliliters
 * @param {Object} mealContext - Object containing offset and mealType
 * @param {Object} profile - User profile object (for default portion)
 * @returns {Object} The meal object ready to be saved
 */
function createFoodMeal(food, portionInfo, mealContext, profile) {
    // Apply defaults
    const unit = portionInfo.unit || 'serving';
    const amount = portionInfo.amount || profile.portion;
    const grams = portionInfo.grams || food.grams_per_serving;
    const milliliters = portionInfo.milliliters || food.milliliters_per_serving;
    
    return {
        id: generateShortUuid(),
        meal_type: 'food',
        food_uuid: food.uuid,
        recipe_title: food.pretty_name || food.name,
        logged_unit: unit,
        logged_amount: amount,
        logged_grams: grams,
        logged_milliliters: milliliters,
        meal: mealContext.mealType.name,
        offset: mealContext.offset,
    };
}

/**
 * Hook that provides food selection functionality with portion handling
 * @param {Object} params - Hook parameters
 * @param {Object} params.modalManager - Modal manager instance
 * @param {Object} params.mealOperations - Meal CRUD operations
 * @param {Object} params.profile - User profile
 * @returns {Object} Food selection handlers
 */
export function useFoodSelection({ modalManager, mealOperations, profile }) {
    /**
     * Handles the complete flow of selecting a food item
     * @param {Object} food - The food object
     * @param {Object} mealContext - Context containing offset, mealType, mealsToDelete
     * @param {Object} initialPortionInfo - Optional initial portion info
     */
    const handleFoodSelection = useCallback((food, mealContext, initialPortionInfo = {}) => {
        const { unit, amount, grams, milliliters } = initialPortionInfo;
        
        // Check if we need portion information from the user
        if (needsPortionInfo(food, unit, grams, milliliters)) {
            // Show the LogPortionsModal to collect portion info
            modalManager.showLogPortions([food], {
                modalTitle: "How much?",
                ctaText: "Add Food",
                requirePrecise: false,
                onSelectPortions: (contents, portions) => {
                    // Extract portion data from the modal response
                    const portionData = extractPortionData(portions, food.uuid);
                    if (portionData) {
                        // Process the food with the collected portion info
                        processFoodWithPortions(food, portionData, mealContext);
                    }
                }
            });
        } else {
            // We have all the info we need, process immediately
            const portionInfo = { unit, amount, grams, milliliters };
            processFoodWithPortions(food, portionInfo, mealContext);
        }
    }, [modalManager, mealOperations, profile]);
    
    /**
     * Processes a food item with complete portion information
     * @param {Object} food - The food object
     * @param {Object} portionInfo - Complete portion information
     * @param {Object} mealContext - Context containing offset, mealType, mealsToDelete
     */
    const processFoodWithPortions = useCallback((food, portionInfo, mealContext) => {
        // Create the meal object
        const newMeal = createFoodMeal(food, portionInfo, mealContext, profile);
        
        // Handle meal replacement or addition
        const mealsToDelete = mealContext.mealsToDelete || [];
        
        // Always use batch operations for consistency
        mealOperations.batchOperations((batchOps) => {
            // Remove any meals that need to be deleted (empty array means forEach won't run)
            mealsToDelete.forEach((meal) => {
                batchOps.removeMeal(meal.id || meal.uuid, true); // hardDelete = true for swaps
            });
            // Add the new meal
            batchOps.addMeals([newMeal]);
        });
        
        // Close the modal
        modalManager.closeModal('addSwapRecipe');
    }, [mealOperations, profile, modalManager]);
    
    /**
     * Creates a handler for the onSelectFood callback expected by AddSwapRecipe
     * @param {Object} mealContext - Context containing offset, mealType, mealsToDelete
     * @returns {Function} Handler function matching the expected signature
     */
    const createOnSelectFoodHandler = useCallback((mealContext) => {
        return (food, unit = null, amount = 1, grams = null, milliliters = null) => {
            const initialPortionInfo = { unit, amount, grams, milliliters };
            handleFoodSelection(food, mealContext, initialPortionInfo);
        };
    }, [handleFoodSelection]);
    
    return {
        handleFoodSelection,
        processFoodWithPortions,
        createOnSelectFoodHandler
    };
}